import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router'
import { matrix } from '@/lib/matrix'

// Path-style deep links (an Atrium room link in a Google Calendar invite, the
// OAuth callback's /calendar?gcal=… redirect) arrive as a real pathname, but
// this app routes in the URL hash. Rewrite /room/x?y → /#/room/x?y before the
// router reads the location, so external links land where they point.
const { pathname, search, hash } = window.location
if (pathname !== '/' && !hash) {
  window.history.replaceState(null, '', '/#' + pathname + search)
}

const routes: RouteRecordRaw[] = [
  { path: '/', redirect: '/floor' },
  { path: '/login', name: 'login', component: () => import('@/views/LoginView.vue'), meta: { bare: true } },
  { path: '/join', name: 'join', component: () => import('@/views/JoinView.vue'), meta: { bare: true } },
  { path: '/floor', name: 'floor', component: () => import('@/views/FloorView.vue') },
  { path: '/room/:slug', name: 'room', component: () => import('@/views/RoomView.vue') },
  { path: '/tasks', name: 'tasks', component: () => import('@/views/TasksView.vue') },
  { path: '/tasks/:board/:ticket', name: 'ticket', component: () => import('@/views/TicketView.vue') },
  { path: '/mentions', name: 'mentions', component: () => import('@/views/MentionsView.vue') },
  { path: '/calendar', name: 'calendar', component: () => import('@/views/CalendarView.vue') },
  { path: '/memory', name: 'memory', component: () => import('@/views/MemoryView.vue') },
  { path: '/settings', name: 'settings', component: () => import('@/views/SettingsView.vue') },
  { path: '/admin', name: 'admin', component: () => import('@/views/AdminView.vue') },
  { path: '/:pathMatch(.*)*', redirect: '/floor' },
]

export const router = createRouter({
  history: createWebHashHistory(),
  routes,
})

// Auth gate: no session → login; logged-in visiting /login → floor. No demo
// bypass. /join is public — it's where invite links land. A deep link (e.g. a
// meeting-room link) survives the bounce via ?next=… so login lands there.
router.beforeEach((to) => {
  const authed = matrix.hasSession()
  if (!authed && to.name !== 'login' && to.name !== 'join') {
    return to.name === 'floor'
      ? { name: 'login' }
      : { name: 'login', query: { next: to.fullPath } }
  }
  if (authed && to.name === 'login') return { name: 'floor' }
  return true
})
